import React from 'react';

const LandingPage = ({ onEnter }) => {
  return (
    <div className="w-full bg-gradient-to-b from-indigo-900 via-purple-900 to-pink-800">
      {/* Hero Section */}
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-pink-800 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-purple-500 rounded-full opacity-20 blur-3xl -top-48 -left-48 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-pink-500 rounded-full opacity-20 blur-3xl -bottom-48 -right-48 animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute w-96 h-96 bg-indigo-500 rounded-full opacity-20 blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto text-center">
          {/* Logo/Icon */}
          <div className="mb-8 flex justify-center">
            <div className="relative">
              <svg className="w-24 h-24 text-white opacity-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
              </svg>
              <div className="absolute inset-0 bg-white opacity-20 blur-xl rounded-full"></div>
            </div>
          </div>

          {/* Main Title */}
          <h1 className="text-7xl md:text-8xl font-light text-white mb-6 tracking-wide">
            ElseWhere
          </h1>

          {/* Tagline */}
          <p className="text-xl md:text-2xl text-white/80 mb-4 font-light">
            Leave your mark on the world
          </p>
          <p className="text-lg text-white/60 mb-12 max-w-2xl mx-auto">
            Share emotions and moments tied to real places.
          </p>

          {/* CTA Button */}
          <button
            onClick={onEnter}
            className="group relative px-12 py-4 bg-white text-gray-900 rounded-full text-lg font-medium hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 shadow-2xl mb-16"
          >
            <span className="relative z-10">Explore the Map</span>
            <svg 
              className="inline-block ml-2 w-5 h-5 transform group-hover:translate-x-1 transition-transform" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </button>

          {/* Scroll indicator */}
          <div className="animate-bounce">
            <svg className="w-6 h-6 text-white/50 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3"/>
            </svg>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="min-h-screen bg-gradient-to-b from-purple-900 to-indigo-900 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute w-96 h-96 bg-indigo-500 rounded-full blur-3xl top-20 left-20"></div>
          <div className="absolute w-96 h-96 bg-purple-500 rounded-full blur-3xl bottom-20 right-20"></div>
        </div>

        <div className="relative z-10 max-w-4xl mx-auto">
          <h2 className="text-5xl md:text-6xl font-light text-white text-center mb-4 tracking-wide">
            How It Works
          </h2>
          <p className="text-white/70 text-center mb-16 max-w-2xl mx-auto">
            ElseWhere is designed to be simple, ephemeral, and authentic
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                </svg>
              </div>
              <h3 className="text-white text-2xl font-light mb-3 text-center">Location-Based</h3>
              <p className="text-white/70 text-center leading-relaxed">
                Pin your emotions to real places on the map. Every mark is tied to a specific location, creating a collective emotional geography.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
              </div>
              <h3 className="text-white text-2xl font-light mb-3 text-center">Living Map</h3>
              <p className="text-white/70 text-center leading-relaxed">
                Marks are community-driven and shaped by ongoing activity around each place.
              </p>
            </div>

            <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20 hover:bg-white/15 transition-all">
              <div className="w-14 h-14 bg-gradient-to-br from-rose-400 to-pink-500 rounded-xl flex items-center justify-center mx-auto mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
                </svg>
              </div>
              <h3 className="text-white text-2xl font-light mb-3 text-center">Anonymous</h3>
              <p className="text-white/70 text-center leading-relaxed">
                No accounts needed. No tracking. Just honest moments. Share your feelings without worrying about your digital identity.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="min-h-screen bg-gradient-to-b from-indigo-900 to-purple-900 flex items-center justify-center p-4 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute w-96 h-96 bg-pink-500 rounded-full blur-3xl top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"></div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center">
          <h2 className="text-5xl md:text-6xl font-light text-white mb-6 tracking-wide">
            Ready to Leave Your Mark?
          </h2>
          <p className="text-white/70 mb-12 text-lg">
            Join a community of people sharing authentic, ephemeral moments tied to real places around the world.
          </p>
          
          <button
            onClick={onEnter}
            className="group relative px-16 py-5 bg-white text-gray-900 rounded-full text-xl font-medium hover:bg-opacity-90 transition-all duration-300 transform hover:scale-105 shadow-2xl"
          >
            <span className="relative z-10">Enter ElseWhere</span>
            <svg 
              className="inline-block ml-2 w-6 h-6 transform group-hover:translate-x-1 transition-transform" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 7l5 5m0 0l-5 5m5-5H6"/>
            </svg>
          </button>

          <p className="text-white/50 text-sm mt-12">
            No account required • Completely anonymous • 100% free
          </p>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
